import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import type { ISheetApprovalRequestState } from "./types";
import {
  getDefaultSelectedRecipientIds,
  getSelectableRecipients,
} from "./utils";

/**
 * Which approvers are ticked, seeded once per opening of the dialog.
 *
 * The seeding runs ONLY on the closed→open transition. React Query may replace
 * `state` on window focus, but the transition guard prevents those updates from
 * restoring defaults over whatever the manager has just ticked.
 *
 * @param open [boolean] whether the dialog is showing
 * @param state [ISheetApprovalRequestState, undefined] latest approval state
 * @param approvalStatus [IPayrollSheetApprovalStatus, null, undefined] gate state
 *   from the workbook payload; decides who is already done and so starts unticked
 */
export const useApprovalRecipientSelection = (
  open: boolean,
  state: ISheetApprovalRequestState | undefined,
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined,
) => {
  const [chosenIds, setChosenIds] = useState<string[]>([]);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setChosenIds(
        state ? getDefaultSelectedRecipientIds(state, approvalStatus) : [],
      );
    }
    wasOpen.current = open;
  }, [approvalStatus, open, state]);

  const selectableIds = useMemo(
    () =>
      state
        ? getSelectableRecipients(state).map((recipient) => recipient.user_id)
        : [],
    [state],
  );

  // Narrowed on read rather than pruned in an effect. A refetch that drops a
  // recipient — reassigned, or their email cleared — would otherwise leave their id
  // in the set: the row vanishes while the count still counts them ("2 of 1"), and
  // submit carries a user the API will reject. Filtering here also means a recipient
  // who briefly disappears and returns keeps their tick.
  const selectedIds = useMemo(
    () => chosenIds.filter((id) => selectableIds.includes(id)),
    [chosenIds, selectableIds],
  );

  const toggle = useCallback((userId: string) => {
    setChosenIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  }, []);

  const toggleAll = useCallback(() => {
    setChosenIds((current) => {
      const stillSelectable = current.filter((id) =>
        selectableIds.includes(id),
      );
      return stillSelectable.length === selectableIds.length
        ? []
        : selectableIds;
    });
  }, [selectableIds]);

  return { selectedIds, toggle, toggleAll };
};
